"use client";

import { type ReactNode, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  QuantityField,
  type SigChoice,
  SigFigsField,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  distanceFromParallax,
  distanceRange,
  PARALLAX_DISTANCE_UNITS,
  PARALLAX_UNITS,
  parallaxFromParsecs,
  parallaxQuality,
} from "@/lib/tool-kit/astronomy/parallax";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";
import { fromBase } from "@/lib/tool-kit/physics-fields/units";

/** English strings. A translated content file overrides these through `ui`. */
export const STELLAR_PARALLAX_CALCULATOR_STRINGS = {
  direction: "Calculate",
  toDistance: "Distance from parallax",
  toParallax: "Parallax from distance",
  star: "Start from a star",
  custom: "Custom value",
  starNames: {
    proxima: "Proxima Centauri (Gaia)",
    barnard: "Barnard's Star (Gaia)",
    sirius: "Sirius (Hipparcos)",
    vega: "Vega (Hipparcos)",
    betelgeuse: "Betelgeuse (Hipparcos)",
  },
  parallax: "Parallax angle p",
  uncertainty: "Uncertainty in p (optional)",
  parallaxUnit: "Unit for the parallax",
  distance: "Distance d",
  distanceUnit: "Unit for the distance",
  sigFigs: "Significant figures",
  sigMessage:
    "one: {count} significant figure|other: {count} significant figures",
  missing: "Enter a value. Scientific notation such as 7.68e-1 works.",
  notPositive: "A parallax or a distance must be greater than zero.",
  results: "Results",
  parsecs: "Distance in parsecs",
  lightYears: "Distance in light-years",
  astronomicalUnits: "Distance in astronomical units",
  kilometres: "Distance in kilometres",
  modulus: "Distance modulus m − M",
  rangeHeading: "Range from the uncertainty",
  rangeLine: "Between {near} and {far} pc ({nearLy} to {farLy} ly).",
  rangeOpen:
    "From {near} pc ({nearLy} ly) outward with no upper limit, because the parallax could be zero.",
  fractional: "Fractional error σ/p",
  percent: "{value}%",
  precise:
    "A precise parallax: inverting it gives a reliable distance, good to about the fractional error shown.",
  usable:
    "A usable parallax, but inverting it is slightly biased. Treat the distance as an estimate and quote the range.",
  poor: "A poor parallax: inverting it gives a biased distance, and the true value could be far larger. Published catalogues use a statistical prior for stars like this.",
  parallaxArcsec: "Parallax in arcseconds",
  parallaxMas: "Parallax in milliarcseconds",
  parallaxUas: "Parallax in microarcseconds",
  formula: "d (parsecs) = 1 ÷ p (arcseconds), so p = 1 ÷ d.",
  copy: "Copy result",
  copied: "Copied!",
  summaryDistance: "Parallax {p} mas: {pc} pc = {ly} ly = {au} AU.",
  summaryParallax: "Distance {d} pc: parallax {mas} mas ({arcsec} arcsec).",
  note: "Distance is 1 ÷ p, the definition of the parsec; the exact tangent form differs by less than one part in 10¹⁰ for any real star. Hipparcos values are from van Leeuwen (2007) and Gaia values from the Gaia Early Data Release 3, as listed by SIMBAD. Gaia parallaxes carry a small zero-point offset of about −0.02 mas that this calculator does not correct.",
} as const;

type Strings = typeof STELLAR_PARALLAX_CALCULATOR_STRINGS;
type Direction = "to-distance" | "to-parallax";

interface Star {
  value: string;
  sigma: string;
}

const STARS: Record<string, Star> = {
  proxima: { value: "768.0665", sigma: "0.0499" },
  barnard: { value: "546.9759", sigma: "0.0401" },
  sirius: { value: "379.21", sigma: "1.58" },
  vega: { value: "130.23", sigma: "0.36" },
  betelgeuse: { value: "6.55", sigma: "0.83" },
};

export default function StellarParallaxCalculatorWidget() {
  const strings: Strings = useToolUi(STELLAR_PARALLAX_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [direction, setDirection] = useState<Direction>("to-distance");
  const [star, setStar] = useState("proxima");
  const [pText, setPText] = useState("768.0665");
  const [sText, setSText] = useState("0.0499");
  const [pUnit, setPUnit] = useState("mas");
  const [dText, setDText] = useState("1.30197");
  const [dUnit, setDUnit] = useState("pc");
  const [sigChoice, setSigChoice] = useState<SigChoice>("5");
  const sig = Number(sigChoice);
  const show = (value: number) => formatSig(fmt, value, sig);

  const parallax = readQuantity(pText, PARALLAX_UNITS, pUnit);
  const sigma = readQuantity(sText, PARALLAX_UNITS, pUnit);
  const parsecsIn = readQuantity(dText, PARALLAX_DISTANCE_UNITS, dUnit);
  const names = strings.starNames as Record<string, string>;

  const pickStar = (id: string) => {
    setStar(id);
    const preset = STARS[id];
    if (preset) {
      setPText(preset.value);
      setSText(preset.sigma);
      setPUnit("mas");
    }
  };

  let body: ReactNode = null;
  if (direction === "to-distance") {
    body = (
      <DistanceResults
        parallax={parallax}
        show={show}
        sigma={sigma}
        strings={strings}
      />
    );
  } else if (parsecsIn === null) {
    body = <Notice>{strings.missing}</Notice>;
  } else if (parsecsIn <= 0) {
    body = <ErrorNotice>{strings.notPositive}</ErrorNotice>;
  } else {
    const arcsec = parallaxFromParsecs(parsecsIn);
    const inUnit = (id: string) => fromBase(PARALLAX_UNITS, id, arcsec);
    body = (
      <>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.parallaxMas}
            value={show(inUnit("mas"))}
          />
          <StatCard label={strings.parallaxArcsec} value={show(arcsec)} />
          <StatCard label={strings.parallaxUas} value={show(inUnit("uas"))} />
        </StatGrid>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.summaryParallax, {
            d: show(parsecsIn),
            mas: show(inUnit("mas")),
            arcsec: show(arcsec),
          })}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="sp-direction"
        onChange={setDirection}
        options={[
          { value: "to-distance", label: strings.toDistance },
          { value: "to-parallax", label: strings.toParallax },
        ]}
        value={direction}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {direction === "to-distance" ? (
          <>
            <SelectField
              id="sp-star"
              label={strings.star}
              onChange={pickStar}
              options={[
                { value: "custom", label: strings.custom },
                ...Object.keys(STARS).map((id) => ({
                  value: id,
                  label: names[id] ?? id,
                })),
              ]}
              value={star}
            />
            <div />
            <QuantityField
              id="sp-parallax"
              label={strings.parallax}
              onChange={(next) => {
                setStar("custom");
                setPText(next);
              }}
              onUnit={setPUnit}
              unit={pUnit}
              unitLabel={strings.parallaxUnit}
              units={PARALLAX_UNITS}
              value={pText}
            />
            <QuantityField
              id="sp-sigma"
              label={strings.uncertainty}
              onChange={(next) => {
                setStar("custom");
                setSText(next);
              }}
              onUnit={setPUnit}
              unit={pUnit}
              unitLabel={strings.parallaxUnit}
              units={PARALLAX_UNITS}
              value={sText}
            />
          </>
        ) : (
          <QuantityField
            id="sp-distance"
            label={strings.distance}
            onChange={setDText}
            onUnit={setDUnit}
            unit={dUnit}
            unitLabel={strings.distanceUnit}
            units={PARALLAX_DISTANCE_UNITS}
            value={dText}
          />
        )}
        <SigFigsField
          format={fmt}
          id="sp-sig"
          label={strings.sigFigs}
          locale={locale}
          message={strings.sigMessage}
          onChange={setSigChoice}
          value={sigChoice}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
        <p className="font-mono text-muted-foreground text-sm">
          {strings.formula}
        </p>
      </section>
      <Notice>{strings.note}</Notice>
    </div>
  );
}

function DistanceResults({
  parallax,
  sigma,
  show,
  strings,
}: {
  parallax: number | null;
  sigma: number | null;
  show: (value: number) => string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  if (parallax === null) {
    return <Notice>{strings.missing}</Notice>;
  }
  if (parallax <= 0) {
    return <ErrorNotice>{strings.notPositive}</ErrorNotice>;
  }
  const d = distanceFromParallax(parallax);
  const lyPerPc = d.lightYears / d.parsecs;
  const spread = sigma !== null && sigma > 0 ? sigma : null;
  const fraction = spread === null ? null : spread / parallax;
  const range = spread === null ? null : distanceRange(parallax, spread);
  const quality = fraction === null ? null : parallaxQuality(fraction);
  const summary = t(strings.summaryDistance, {
    p: show(parallax * 1000),
    pc: show(d.parsecs),
    ly: show(d.lightYears),
    au: show(d.astronomicalUnits),
  });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.parsecs} value={show(d.parsecs)} />
        <StatCard
          emphasis
          label={strings.lightYears}
          value={show(d.lightYears)}
        />
        <StatCard
          label={strings.astronomicalUnits}
          value={show(d.astronomicalUnits)}
        />
        <StatCard label={strings.kilometres} value={show(d.kilometres)} />
        <StatCard
          label={strings.modulus}
          value={fmt(d.distanceModulus, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        />
      </StatGrid>
      {range && fraction !== null && quality ? (
        <div className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.rangeHeading}
          </p>
          <p className="text-foreground text-sm">
            {range.far === null
              ? t(strings.rangeOpen, {
                  near: show(range.near),
                  nearLy: show(range.near * lyPerPc),
                })
              : t(strings.rangeLine, {
                  near: show(range.near),
                  far: show(range.far),
                  nearLy: show(range.near * lyPerPc),
                  farLy: show(range.far * lyPerPc),
                })}
          </p>
          <p className="text-foreground text-sm">
            {strings.fractional}:{" "}
            {t(strings.percent, {
              value: fmt(fraction * 100, { maximumFractionDigits: 2 }),
            })}
          </p>
          <Notice>{strings[quality]}</Notice>
        </div>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={summary}
      />
    </>
  );
}
